/**
 * Message randomization utilities for recording states
 */

// Re-export shared function from package
export { getShortRecordingMessage } from '@innerhive-internal/innerhive-common';

export function getIntroSuccessMessage(firstName: string): { title: string; message: string } {
    return {
        title: `Thanks for sharing, ${firstName}.`,
        message: "I'm putting together your care profile. It should take about a minute. From now on I'll remember the things you share, so each time we talk I can be a little more helpful."
    };
}

/**
 * Copy for a recording that will never be summarized. Every other message here says a summary is
 * coming, which for these recordings is a promise nothing will keep. Only automated tests reach
 * this today, but it stays honest for whatever else ever skips processing.
 */
export function getNoSummaryMessage({ isOffline }: { isOffline?: boolean }): { title: string; message: string } {
    return {
        title: isOffline ? 'Recording saved offline!' : 'Recording saved!',
        message: isOffline
            ? 'Your recording will be added to your notes once you are back online.'
            : 'Your recording is saved to your notes.'
    };
}

export function getSuccessMessage(isOffline?: boolean): { title: string; message: string } {
    if (isOffline) {
        // Offline-specific messages emphasizing sync when online
        const offlineMessages = [
            { title: 'Recording saved offline!', message: 'We\'ll organize it and create your summary once you\'re back online.' },
            { title: 'Recording secured offline!', message: 'We\'ll sync and prepare your summary when connected.' },
            { title: 'Recording saved offline!', message: 'Organizing will start once you\'re online.' },
            { title: 'Recording stored offline!', message: 'We\'ll turn this into notes when you reconnect.' },
            { title: 'Recording safely stored offline!', message: 'It will be organized and summarized once you\'re online.' },
            { title: 'Recording captured offline!', message: 'We\'ll organize it as soon as you\'re connected again.' }
        ];
        return offlineMessages[Math.floor(Math.random() * offlineMessages.length)];
    } else {
        // Online messages for immediate processing
        const onlineMessages = [
            { title: 'Recording saved!', message: 'We\'re creating organized notes — sit tight, we\'ll notify you when they\'re ready.' },
            { title: 'Recording safe!', message: 'We\'re turning it into highlights and next steps — you\'ll hear from us as soon as they\'re ready.' },
            { title: 'Recording secure!', message: 'We\'re shaping it into a summary — almost there! We\'ll notify you once it\'s ready.' },
            { title: 'Recording captured!', message: 'We\'re pulling out key points and next steps — sit tight, you\'ll be notified when they\'re ready.' },
            { title: 'Recording saved!', message: 'We\'re turning it into clear notes — we\'ll notify you as soon as they\'re ready.' },
            { title: 'Recording complete!', message: 'We\'ll take it from here — you\'ll get a notification when your summary is ready.' },
            { title: 'Recording received!', message: 'We\'re extracting the highlights — we\'ll let you know when they\'re polished and ready.' },
            { title: 'Recording saved!', message: 'We\'re breaking this down into next steps and insights — you\'ll be notified shortly.' }
        ];
        return onlineMessages[Math.floor(Math.random() * onlineMessages.length)];
    }
}
